import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import { get } from "lib/api";
import { firstMediaUrl, firstThumbnailMediaUrl, mediaUrl, thumbnailMediaUrl } from "lib/media";
import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { currencyForCompany } from "lib/companyProfile";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import {
  hasHtml,
  normalizeSpecEntries,
  normalizeTechnicalSections,
  normalizeTransmissionNames,
  productStoryText,
} from "storefronts/shared/pimProductContent";
import MxShell from "../components/MxShell";
import { formatMoney, mx, resolvePrice } from "../lib/theme";

function normalizeProduct(data) {
  if (!data?.data) return null;
  return data.data.attributes
    ? { id: data.data.id, documentId: data.data.documentId, ...data.data.attributes }
    : { ...data.data, documentId: data.data.documentId };
}

function RichText({ value, sx }) {
  if (!value) return null;
  if (hasHtml(value)) {
    return (
      <Box
        sx={{ fontSize: 15, lineHeight: 1.6, color: mx.muted, "& p": { m: 0 }, ...sx }}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }
  return (
    <Typography sx={{ fontSize: 15, lineHeight: 1.6, color: mx.muted, whiteSpace: "pre-wrap", ...sx }}>
      {value}
    </Typography>
  );
}

RichText.propTypes = {
  value: PropTypes.string,
  sx: PropTypes.object,
};

RichText.defaultProps = {
  value: "",
  sx: undefined,
};

export default function ProductDetailPage() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { addItem, lines } = useCart();
  const { token, storefrontTenant } = useAuth();
  const { t } = useStorefrontCopy();
  const isTablet = useMediaQuery(`(min-width:${mx.tabletBp}px)`);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ["mx-mobile-product", documentId, storefrontTenant || "mx", token ? "auth" : "public"],
    queryFn: () => get(`/api/storefront/catalog/${documentId}`),
    enabled: !!documentId,
    staleTime: 30000,
  });

  const currency = data?.meta?.company?.baseCurrency || currencyForCompany(storefrontTenant || "mx");
  const product = normalizeProduct(data);
  const effectivePrice = resolvePrice(product);
  const hasPrice = Number(effectivePrice) > 0;
  const coreCharge = Number(product?.coreCharge || 0);
  const images = Array.isArray(product?.images?.data)
    ? product.images.data
    : Array.isArray(product?.images)
      ? product.images
      : [];
  const selectedImage =
    mediaUrl(images[selectedImageIndex], { size: "medium" }) ||
    mediaUrl(images[selectedImageIndex]) ||
    firstMediaUrl(product?.images, { size: "medium" }) ||
    firstMediaUrl(product?.images);
  const cartThumbUrl =
    thumbnailMediaUrl(images[selectedImageIndex]) ||
    firstThumbnailMediaUrl(product?.images) ||
    selectedImage;
  const inCartQty =
    (lines || []).find((line) => String(line.documentId) === String(product?.documentId))?.qty || 0;
  const transmissions = normalizeTransmissionNames(product?.transmissions);
  const story = productStoryText(product);
  const unitDetails = String(product?.unitSpecificDetails || "").trim();
  const installationNotes = String(product?.installationNotes || "").trim();
  const fitmentYears = String(product?.fitmentYears || "").trim();
  const specEntries = normalizeSpecEntries(product?.specifications);
  const technicalSections = normalizeTechnicalSections(product?.technicalSections);

  const addToCart = (goToCart = false) => {
    if (!hasPrice || !product) return;
    addItem(
      {
        documentId: product.documentId,
        sku: product.sku,
        name: product.name,
        unitPrice: Number(effectivePrice),
        coreCharge: Number.isFinite(coreCharge) && coreCharge > 0 ? coreCharge : null,
        imageUrl: cartThumbUrl,
      },
      quantity,
    );
    if (goToCart) navigate("/m/cart");
  };

  const footer =
    !isLoading && product && !error ? (
      !token ? (
        <ButtonBase
          onClick={() => navigate("/m/authentication/sign-in")}
          sx={{
            width: "100%",
            bgcolor: mx.accent,
            color: "#fff",
            py: 1.45,
            borderRadius: "8px",
            fontSize: 15,
            fontWeight: 800,
            boxShadow: "0 10px 24px rgba(255,92,0,0.28)",
            "&:hover": { bgcolor: mx.accentHover },
          }}
        >
          {t("product.signInToOrder", "Inicie sesión para ordenar")}
        </ButtonBase>
      ) : hasPrice ? (
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              border: `1px solid ${mx.hairline}`,
              borderRadius: "10px",
              bgcolor: mx.canvas,
              px: 0.25,
            }}
          >
            <ButtonBase
              onClick={() => setQuantity((value) => Math.max(1, value - 1))}
              sx={{ width: 40, height: 40, color: mx.ink, borderRadius: "8px" }}
            >
              <Icon sx={{ fontSize: 18 }}>remove</Icon>
            </ButtonBase>
            <Typography sx={{ width: 28, textAlign: "center", fontWeight: 800, color: mx.ink }}>
              {quantity}
            </Typography>
            <ButtonBase
              onClick={() => setQuantity((value) => Math.min(99, value + 1))}
              sx={{ width: 40, height: 40, color: mx.ink, borderRadius: "8px" }}
            >
              <Icon sx={{ fontSize: 18 }}>add</Icon>
            </ButtonBase>
          </Stack>
          <ButtonBase
            onClick={() => addToCart(true)}
            sx={{
              flex: 1,
              bgcolor: mx.accent,
              color: "#fff",
              py: 1.45,
              px: 2,
              borderRadius: "8px",
              fontSize: 15,
              fontWeight: 800,
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              boxShadow: "0 10px 24px rgba(255,92,0,0.28)",
              "&:hover": { bgcolor: mx.accentHover },
            }}
          >
            <span>{t("catalog.addToCart", "Agregar al carrito")}</span>
            <span>{formatMoney(Number(effectivePrice) * quantity, currency)}</span>
          </ButtonBase>
        </Stack>
      ) : (
        <Typography sx={{ textAlign: "center", color: mx.muted, py: 0.75, fontSize: 14.5 }}>
          {t("product.contactPricing", "Contáctenos para cotizar este artículo.")}
        </Typography>
      )
    ) : null;

  return (
    <MxShell onBack={() => navigate("/m/catalog")} footer={footer}>
      {isLoading ? (
        <Typography sx={{ py: 6, textAlign: "center", color: mx.muted }}>
          {t("product.loadingProduct", "Cargando producto…")}
        </Typography>
      ) : error || !product ? (
        <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: mx.ink }}>
            {t("product.notFound", "Producto no encontrado")}
          </Typography>
          <ButtonBase
            onClick={() => navigate("/m/catalog")}
            sx={{
              mt: 2,
              bgcolor: mx.accent,
              color: "#fff",
              px: 2.5,
              py: 1.25,
              borderRadius: "8px",
              fontWeight: 750,
            }}
          >
            {t("product.backToCatalog", "Volver al catálogo")}
          </ButtonBase>
        </Box>
      ) : (
        <Box
          sx={{
            maxWidth: mx.tabletMax,
            mx: "auto",
            display: "grid",
            gridTemplateColumns: isTablet ? "1.05fr 0.95fr" : "1fr",
            gap: isTablet ? 3 : 0,
            alignItems: "start",
            px: isTablet ? 3 : 0,
            pt: isTablet ? 2 : 0,
          }}
        >
          <Box>
            <Box
              sx={{
                bgcolor: "#EEF0F3",
                aspectRatio: isTablet ? "1 / 1" : "1 / 1.05",
                display: "grid",
                placeItems: "center",
                overflow: "hidden",
                borderRadius: isTablet ? "12px" : 0,
                mx: isTablet ? 0 : -2,
              }}
            >
              {selectedImage ? (
                <Box
                  component="img"
                  src={selectedImage}
                  alt={product.name}
                  decoding="async"
                  sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                />
              ) : (
                <Icon sx={{ fontSize: 48, color: "rgba(15,15,18,0.2)" }}>image</Icon>
              )}
            </Box>

            {images.length > 1 ? (
              <Stack
                direction="row"
                spacing={0.85}
                sx={{
                  overflowX: "auto",
                  px: isTablet ? 0 : 0,
                  py: 1.5,
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {images.map((image, index) => {
                  const url = thumbnailMediaUrl(image) || mediaUrl(image);
                  if (!url) return null;
                  const active = index === selectedImageIndex;
                  return (
                    <ButtonBase
                      key={url}
                      onClick={() => setSelectedImageIndex(index)}
                      sx={{
                        width: 64,
                        height: 64,
                        flex: "0 0 64px",
                        overflow: "hidden",
                        borderRadius: "8px",
                        border: active ? `2px solid ${mx.accent}` : `1px solid ${mx.hairline}`,
                        bgcolor: "#fff",
                      }}
                    >
                      <Box
                        component="img"
                        src={url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </ButtonBase>
                  );
                })}
              </Stack>
            ) : null}
          </Box>

          <Box sx={{ pt: isTablet ? 0 : 0.5, pb: 2 }}>
            <Typography
              sx={{
                fontFamily: mx.fontMono,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.9,
                color: mx.accent,
                textTransform: "uppercase",
              }}
            >
              {product.sku || "SKU"}
            </Typography>
            <Typography
              sx={{
                mt: 0.6,
                fontSize: { xs: 26, sm: 30 },
                fontWeight: 800,
                letterSpacing: -0.45,
                lineHeight: 1.1,
                color: mx.ink,
              }}
            >
              {product.name}
            </Typography>

            {(transmissions.length || fitmentYears) ? (
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                {fitmentYears ? (
                  <Box
                    sx={{
                      px: 1.1,
                      py: 0.55,
                      borderRadius: "999px",
                      bgcolor: mx.accentSoft,
                      color: mx.accentHover,
                      fontSize: 12.5,
                      fontWeight: 700,
                    }}
                  >
                    {t("product.fitmentYears", "Aplicación")}: {fitmentYears}
                  </Box>
                ) : null}
                {transmissions.map((item) => (
                  <Box
                    key={item}
                    sx={{
                      px: 1.1,
                      py: 0.55,
                      borderRadius: "999px",
                      bgcolor: mx.accentSoft,
                      color: mx.accentHover,
                      fontSize: 12.5,
                      fontWeight: 700,
                    }}
                  >
                    {item}
                  </Box>
                ))}
              </Stack>
            ) : null}

            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: "12px",
                bgcolor: mx.surface,
                border: `1px solid ${mx.hairline}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11.5,
                  fontWeight: 800,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  color: mx.subtle,
                }}
              >
                {t("product.unitPrice", "Precio unitario")}
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  fontSize: 32,
                  fontWeight: 800,
                  letterSpacing: -0.6,
                  color: mx.ink,
                  fontFamily: mx.fontMono,
                }}
              >
                {formatMoney(effectivePrice, currency, t("catalog.priceQuote", "Cotización"))}
              </Typography>
              {coreCharge > 0 ? (
                <Typography sx={{ mt: 0.5, fontSize: 14, color: mx.muted }}>
                  + {formatMoney(coreCharge, currency)}{" "}
                  {t("product.coreDeposit", "depósito de core (reembolsable)")}
                </Typography>
              ) : null}
              {inCartQty ? (
                <Typography sx={{ mt: 0.85, fontSize: 13.5, fontWeight: 750, color: "#147A5C" }}>
                  {inCartQty} {t("product.alreadyInCart", "en el carrito")}
                </Typography>
              ) : null}
            </Box>

            {story ? (
              <Box sx={{ mt: 2.75 }}>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: mx.subtle,
                    mb: 1,
                  }}
                >
                  {t("product.details", "Detalles")}
                </Typography>
                <RichText value={story} />
              </Box>
            ) : null}

            {unitDetails ? (
              <Box sx={{ mt: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: mx.subtle,
                    mb: 1,
                  }}
                >
                  {t("product.unitDetails", "Detalles de la unidad")}
                </Typography>
                <RichText value={unitDetails} />
              </Box>
            ) : null}

            {specEntries.length ? (
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: "12px",
                  bgcolor: mx.surface,
                  border: `1px solid ${mx.hairline}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: mx.subtle,
                    mb: 1,
                  }}
                >
                  {t("product.specifications", "Especificaciones")}
                </Typography>
                {specEntries.map(([key, val]) => (
                  <Box
                    key={key}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1.2fr",
                      gap: 1.25,
                      py: 0.9,
                      borderBottom: `1px solid ${mx.hairline}`,
                    }}
                  >
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: mx.muted }}>{key}</Typography>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: mx.ink,
                        fontFamily: mx.fontMono,
                        textAlign: "right",
                      }}
                    >
                      {val}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : null}

            {installationNotes ? (
              <Box sx={{ mt: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: mx.subtle,
                    mb: 1,
                  }}
                >
                  {t("product.installationNotes", "Notas de instalación")}
                </Typography>
                <RichText value={installationNotes} />
              </Box>
            ) : null}

            {technicalSections.length ? (
              <Box sx={{ mt: 2.5, display: "grid", gap: 1.5 }}>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    color: mx.subtle,
                  }}
                >
                  {t("product.technicalDetails", "Detalles del producto")}
                </Typography>
                {technicalSections.map((section, index) => (
                  <Box
                    key={`${section.title || "section"}-${index}`}
                    sx={{
                      p: 2,
                      borderRadius: "12px",
                      bgcolor: mx.surface,
                      border: `1px solid ${mx.hairline}`,
                    }}
                  >
                    {section.title ? (
                      <Typography sx={{ mb: 0.75, fontSize: 15.5, fontWeight: 800, color: mx.ink }}>
                        {section.title}
                      </Typography>
                    ) : null}
                    {section.body ? <RichText value={section.body} /> : null}
                    {section.images.length ? (
                      <Box
                        sx={{
                          mt: 1.25,
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 1,
                        }}
                      >
                        {section.images.map((img, imageIndex) => {
                          const url = mediaUrl(img) || thumbnailMediaUrl(img);
                          if (!url) return null;
                          return (
                            <Box
                              key={`${index}-${imageIndex}`}
                              component="img"
                              src={url}
                              alt={section.title || t("product.technicalDetails", "Detalles del producto")}
                              loading="lazy"
                              decoding="async"
                              sx={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                                objectFit: "cover",
                                borderRadius: "8px",
                                bgcolor: "#EEF0F3",
                              }}
                            />
                          );
                        })}
                      </Box>
                    ) : null}
                  </Box>
                ))}
              </Box>
            ) : null}

            {token && hasPrice ? (
              <ButtonBase
                onClick={() => addToCart(false)}
                sx={{
                  mt: 2.5,
                  width: "100%",
                  py: 1.35,
                  borderRadius: "8px",
                  border: `1.5px solid ${mx.ink}`,
                  color: mx.ink,
                  fontSize: 15,
                  fontWeight: 750,
                  "&:hover": { bgcolor: "rgba(15,15,18,0.04)" },
                }}
              >
                {t("catalog.addToCart", "Agregar al carrito")}
              </ButtonBase>
            ) : null}
          </Box>
        </Box>
      )}
    </MxShell>
  );
}
